import React, { useEffect, useMemo, useRef, useState, startTransition } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import ProductCard from "./ProductCard";
import CachedImage from "./CachedImage";
import CachedVideo from "./CachedVideo";
import {
  KIOSK_GROUPS,
  getCategoryGroup,
  getCategoryIcon,
} from "../config/kiosk";
import "swiper/css";
import "swiper/css/pagination";
import "./KioskMenu.css";

const ALL = "__all__";

// Find the veg / non-veg diet tags the same way the main menu does.
const findDietTags = (tags = []) => {
  const veg = tags.find((t) => {
    const n = (t?.name || "").toLowerCase();
    return n.includes("veg") && !n.includes("non");
  });
  const nonVeg = tags.find((t) => {
    const n = (t?.name || "").toLowerCase();
    return n.includes("non") && n.includes("veg");
  });
  return { veg, nonVeg };
};

// Pay-from-the-menu links get a lean header: no search box and no banners —
// just the café, the Veg switch and the cart, on one line. This turns on only
// when both billing gates agree (?allowBilling param + backend billingEnable),
// passed down as `billing` so it never out-runs what the cart/checkout allows.
export default function KioskMenu({
  cafeName,
  categories = [],
  tags = [],
  selectedTags = [],
  onTagToggle,
  onProductClick,
  onAddToCart,
  onDecreaseFromCart,
  getProductCartQty,
  getProductCartLineCount,
  soldOutItems,
  isCartEnabled = false,
  billing = false,
  searchTerm = "",
  onSearchTermChange,
  highlightText,
  searchCategories = [],
  cartItemCount = 0,
  onViewCart,
  banners = [],
  onBannerClick,
  showCafeSelector = false,
  onSelectCafe,
}) {
  const BILLING = billing;
  const [activeCategoryId, setActiveCategoryId] = useState(categories[0]?.id);
  const [activeSub, setActiveSub] = useState(ALL);
  const scrollRef = useRef(null);
  // Billing header: the search box stays folded behind an icon until it's tapped.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);

  // Keep the active category valid as the list changes (e.g. veg filter applied).
  // Note: `categories` (the rail) is filtered by diet only, never by the search
  // text, so this never fires just because the guest is typing a search.
  useEffect(() => {
    if (!categories.length) return;
    if (!categories.some((c) => c.id === activeCategoryId)) {
      setActiveCategoryId(categories[0].id);
      setActiveSub(ALL);
    }
  }, [categories, activeCategoryId]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === activeCategoryId) || categories[0],
    [categories, activeCategoryId]
  );

  // Reset scroll + subcategory when switching categories. Picking a category
  // from the rail always exits search — the rail is a browse action, not a
  // search refinement.
  const selectCategory = (id) => {
    setActiveCategoryId(id);
    setActiveSub(ALL);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    if (searchTerm) onSearchTermChange?.("");
  };

  const selectSub = (sub) => {
    setActiveSub(sub);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  // Group categories into the rail (Order now / Take home), preserving order.
  const railGroups = useMemo(() => {
    const groups = [];
    let current = null;
    categories.forEach((cat) => {
      const g = getCategoryGroup(cat);
      if (!current || current.key !== g) {
        current = { key: g, label: KIOSK_GROUPS[g], items: [] };
        groups.push(current);
      }
      current.items.push(cat);
    });
    return groups;
  }, [categories]);

  const { veg } = findDietTags(tags);
  const vegActive = veg && selectedTags.includes(veg.id);
  const toggleVeg = () => {
    if (veg && onTagToggle) onTagToggle(veg.id);
  };
  // Billing header switch is Veg <-> All, like the tablet menu: off shows everything,
  // on shows vegetarian only. (There is no separate "Non-Veg only" state.)
  const dietSwitch = BILLING && veg;
  const dietMode = vegActive ? "veg" : "all";

  // The control moves the instant it is tapped (its own state), while the list behind it is re-filtered as a
  // low-priority update — so the highlight glides and the dishes follow, instead of the tap waiting on 150 cards.
  const [dietUI, setDietUI] = useState(dietMode);
  useEffect(() => { setDietUI(dietMode); }, [dietMode]);
  const setDiet = (next) => {
    if (!veg || next === dietUI) return;
    setDietUI(next);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;   // the dishes below change: start from the top, like picking a category
    startTransition(() => {
      if ((next === "veg") !== Boolean(vegActive)) toggleVeg();
    });
  };
  // A short fade whenever the filter changes the list, so it doesn't just cut from one set of dishes to another.
  const listRef = useRef(null);
  const dietSeen = useRef(dietMode);
  useEffect(() => {
    if (dietSeen.current === dietMode) return;
    dietSeen.current = dietMode;
    const el = listRef.current;
    if (!el) return;
    el.classList.remove("kiosk-swap");
    void el.offsetWidth;            // restart the animation
    el.classList.add("kiosk-swap");
  }, [dietMode]);

  const closeSearch = () => {
    onSearchTermChange?.("");
    setSearchOpen(false);
  };
  useEffect(() => {
    if (BILLING && searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  // Subcategory names that still have products in the active category.
  const availableSubs = useMemo(() => {
    if (!activeCategory) return [];
    const subNames = (activeCategory.subCategories || []).map((s) => s.name);
    return subNames.filter((name) =>
      (activeCategory.products || []).some((p) => p.subCategoryName === name)
    );
  }, [activeCategory]);

  const cardProps = (product) => ({
    product,
    tags,
    variant: "tile",
    onClick: onProductClick,
    onAddToCart,
    onDecreaseFromCart,
    cartQuantity: getProductCartQty ? getProductCartQty(product) : 0,
    cartLineCount: getProductCartLineCount ? getProductCartLineCount(product) : 0,
    highlightText,
    searchTerm,
    isSoldOut: false,
    isCartEnabled,
  });

  const trimmedSearch = (searchTerm || "").trim();
  const isSearching = Boolean(trimmedSearch);

  // Build the item area: grouped by subcategory when "All", flat when filtered.
  const renderItems = () => {
    if (!activeCategory) return null;
    const products = activeCategory.products || [];

    if (activeSub !== ALL) {
      const filtered = products.filter((p) => p.subCategoryName === activeSub);
      return (
        <div className="kiosk-grid">
          {filtered.map((p) => <ProductCard key={p.id} {...cardProps(p)} />)}
        </div>
      );
    }

    const subNames = availableSubs;
    if (subNames.length === 0) {
      return (
        <div className="kiosk-grid">
          {products.map((p) => <ProductCard key={p.id} {...cardProps(p)} />)}
        </div>
      );
    }

    const sections = subNames.map((name) => ({
      name,
      items: products.filter((p) => p.subCategoryName === name),
    }));
    const leftovers = products.filter(
      (p) => !subNames.includes(p.subCategoryName)
    );

    return (
      <>
        {sections.map((sec) => (
          <div key={sec.name} className="kiosk-section">
            <h4 className="kiosk-section-title">{sec.name}</h4>
            <div className="kiosk-grid">
              {sec.items.map((p) => <ProductCard key={p.id} {...cardProps(p)} />)}
            </div>
          </div>
        ))}
        {leftovers.length > 0 && (
          <div className="kiosk-section">
            <div className="kiosk-grid">
              {leftovers.map((p) => <ProductCard key={p.id} {...cardProps(p)} />)}
            </div>
          </div>
        )}
      </>
    );
  };

  // A dish can sit in several categories (e.g. Newly Launched + Other Beverages);
  // in search results it should appear once, under the first category that has it.
  const seenSearchKeys = new Set();
  const dedupedSearchCategories = searchCategories
    .map((cat) => ({
      ...cat,
      products: (cat.products || []).filter((p) => {
        const key = p?.id || p?.skuCode || p?.name;
        if (!key || seenSearchKeys.has(key)) return false;
        seenSearchKeys.add(key);
        return true;
      }),
    }))
    .filter((cat) => cat.products.length > 0);

  // Build the search results area: grouped by category (and subcategory,
  // when present), across the whole menu — independent of the rail selection.
  const renderSearchResults = () => (
    <>
      {dedupedSearchCategories.map((cat) => {
        const products = cat.products || [];
        const subNames = (cat.subCategories || [])
          .map((s) => s.name)
          .filter((name) => products.some((p) => p.subCategoryName === name));

        if (subNames.length === 0) {
          return (
            <div key={cat.id} className="kiosk-section">
              <h4 className="kiosk-search-category-title">{cat.parentName}</h4>
              <div className="kiosk-grid">
                {products.map((p) => <ProductCard key={p.id} {...cardProps(p)} />)}
              </div>
            </div>
          );
        }

        const sections = subNames.map((name) => ({
          name,
          items: products.filter((p) => p.subCategoryName === name),
        }));
        const leftovers = products.filter((p) => !subNames.includes(p.subCategoryName));

        return (
          <div key={cat.id} className="kiosk-section">
            <h4 className="kiosk-search-category-title">{cat.parentName}</h4>
            {sections.map((sec) => (
              <div key={sec.name} className="kiosk-section">
                <h5 className="kiosk-section-title">{sec.name}</h5>
                <div className="kiosk-grid">
                  {sec.items.map((p) => <ProductCard key={p.id} {...cardProps(p)} />)}
                </div>
              </div>
            ))}
            {leftovers.length > 0 && (
              <div className="kiosk-grid">
                {leftovers.map((p) => <ProductCard key={p.id} {...cardProps(p)} />)}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  // On-brand empty state for both "no search matches" and "filter leaves
  // nothing in this category", themed to match the rest of the kiosk shell.
  const renderEmptyState = () => {
    const title = isSearching
      ? `We couldn't find "${trimmedSearch}"`
      : "Nothing here yet";
    const copy = isSearching
      ? vegActive
        ? "No vegetarian item matches this search. Try another name, or clear the veg filter."
        : "Try a shorter name, a different spelling, or pick a category from the menu on the left."
      : "No items match the current filter. Try clearing it or pick another category.";

    return (
      <div className="kiosk-empty-state">
        <div className="kiosk-empty-icon" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="10.5" cy="10.5" r="7" />
            <line x1="20" y1="20" x2="15.8" y2="15.8" />
          </svg>
        </div>
        <p className="kiosk-empty-eyebrow">{isSearching ? "No matches yet" : "Nothing here"}</p>
        <h4 className="kiosk-empty-title">{title}</h4>
        <p className="kiosk-empty-copy">{copy}</p>
        {(isSearching || vegActive) && (
          <div className="kiosk-empty-actions">
            {isSearching && (
              <button
                type="button"
                className="kiosk-empty-primary"
                onClick={() => onSearchTermChange?.("")}
              >
                Clear search
              </button>
            )}
            {vegActive && (
              <button type="button" className="kiosk-empty-secondary" onClick={toggleVeg}>
                Clear Veg filter
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const searchShownCount = dedupedSearchCategories.reduce(
    (total, cat) => total + (cat.products?.length || 0),
    0
  );

  const shownCount = isSearching
    ? searchShownCount
    : activeCategory
    ? (activeSub === ALL
        ? activeCategory.products.length
        : activeCategory.products.filter((p) => p.subCategoryName === activeSub).length)
    : 0;

  return (
    <div className="kiosk">
      {/* Top bar — search lives inside the header so it can sit inline on desktop */}
      <header className={`kiosk-top ${BILLING ? "kiosk-top-billing" : ""} ${BILLING && searchOpen ? "search-open" : ""}`}>
        <div className="kiosk-brand">
          <img
            src={BILLING ? "/310x3102.png" : "/BTC_Blue_Paradise.png"}
            alt="Blue Tokai"
            className={`kiosk-logo ${BILLING ? "kiosk-logo-round" : ""}`}
          />
          <div className="kiosk-brand-text">
            {!BILLING && <b>Blue Tokai</b>}
            <span className="kiosk-cafe-line">
              {BILLING && (
                <svg className="kiosk-pin" width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 21s7-6.2 7-11.2A7 7 0 0 0 5 9.8C5 14.8 12 21 12 21Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
                  <circle cx="12" cy="9.8" r="2.4" fill="currentColor" />
                </svg>
              )}
              <span className="kiosk-cafe-name">{cafeName}</span>
            </span>
          </div>
        </div>
        {(!BILLING || searchOpen) && (
        <div className={`kiosk-search ${BILLING ? "kiosk-search-billing" : ""}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            ref={searchInputRef}
            type="text"
            inputMode="search"
            className="kiosk-search-input"
            placeholder="Search the menu…"
            value={searchTerm}
            onChange={(e) => onSearchTermChange?.(e.target.value)}
            aria-label="Search the menu"
          />
          {(isSearching || BILLING) && (
            <button
              type="button"
              className="kiosk-search-clear"
              onClick={BILLING ? closeSearch : () => onSearchTermChange?.("")}
              aria-label={BILLING ? "Close search" : "Clear search"}
            >
              ×
            </button>
          )}
        </div>
        )}
        <div className="kiosk-top-right">
          {BILLING && !searchOpen && (
            <button type="button" className="kiosk-searchbtn" onClick={() => setSearchOpen(true)} aria-label="Search the menu">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </button>
          )}
          {dietSwitch ? (
            // Pay-from-the-menu header: "All" and "Veg" side by side, the chosen one filled (navy / green).
            // Both words are always visible, so there is nothing to work out about which way it is set.
            <div className={`kiosk-diet ${dietUI}`} role="group" aria-label="Show">
              <span className="kiosk-diet-thumb" aria-hidden="true" />
              <button type="button" className="kiosk-diet-opt" aria-pressed={dietUI === "all"} onClick={() => setDiet("all")}>
                All
              </button>
              <button type="button" className="kiosk-diet-opt" aria-pressed={dietUI === "veg"} onClick={() => setDiet("veg")}>
                <span className="kiosk-diet-mark" aria-hidden="true" />
                Veg
              </button>
            </div>
          ) : veg && (
            <button
              type="button"
              className={`kiosk-veg ${vegActive ? "on" : ""}`}
              onClick={toggleVeg}
              role="switch"
              aria-checked={vegActive}
            >
              <span className="kiosk-veg-sw" />
              Veg
            </button>
          )}
          {/* A pay-from-the-menu link is already tied to one café/table: no café switcher. */}
          {showCafeSelector && !BILLING && (
            <button type="button" className="kiosk-select-cafe" onClick={onSelectCafe}>
              Select Café
            </button>
          )}
          {isCartEnabled && (
            <button type="button" className="kiosk-cart" onClick={onViewCart}>
              {BILLING ? (
                <svg className="kiosk-bag" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5.5 8.5h13l-1 11h-11l-1-11Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M9 8.5V7a3 3 0 0 1 6 0v1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              ) : (
                <span aria-hidden="true">🛒</span>
              )}{" "}
              <span className="kiosk-cart-label">Cart</span>
              {(!BILLING || cartItemCount > 0) && <span key={cartItemCount} className="kiosk-cart-n">{cartItemCount}</span>}
            </button>
          )}
        </div>
      </header>

      {/* Body: rail + panel */}
      <div className="kiosk-body">
        <nav className="kiosk-rail" aria-label="Menu categories">
          <div className="kiosk-rail-list">
          {railGroups.map((group, groupIndex) => (
            // A group key can repeat when categories of the two groups interleave
            // (order is preserved on purpose), so the index keeps the React key unique.
            <div key={`${group.key}-${groupIndex}`} className="kiosk-rail-group">
              <div className="kiosk-rail-head">{group.label}</div>
              {group.items.map((cat) => {
                const isActive = cat.id === activeCategory?.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    className={`kiosk-cat ${isActive ? "active" : ""}`}
                    onClick={() => selectCategory(cat.id)}
                    aria-current={isActive ? "true" : undefined}
                  >
                    <span className="kiosk-cat-ic">
                      {cat.categoryImage
                        ? <img src={cat.categoryImage} alt="" className="kiosk-cat-img" />
                        : getCategoryIcon(cat)}
                    </span>
                    <span className="kiosk-cat-label">{cat.parentName}</span>
                  </button>
                );
              })}
            </div>
          ))}
          </div>
        </nav>

        <div className={`kiosk-main ${BILLING && !isSearching ? "no-head" : ""}`} ref={scrollRef}>
          {!BILLING && banners.length > 0 && (
            <div className="kiosk-banners">
              <Swiper
                modules={[Autoplay, Pagination]}
                slidesPerView={1}
                spaceBetween={14}
                loop={banners.length > 1}
                autoplay={banners.length > 1 ? { delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true } : false}
                pagination={banners.length > 1 ? { clickable: true } : false}
                grabCursor
                className="kiosk-banner-swiper"
                breakpoints={{
                  700: { slidesPerView: 2, spaceBetween: 16 },
                  1440: { slidesPerView: 3, spaceBetween: 18 },
                }}
              >
                {banners.map((banner) => (
                  <SwiperSlide key={banner.id}>
                    <div
                      className="kiosk-banner-card"
                      onClick={() => onBannerClick?.(banner)}
                      style={{ cursor: banner.redirectMode ? "pointer" : "default" }}
                    >
                      {banner.mediaType === "video" ? (
                        <CachedVideo
                          className="kiosk-banner-media"
                          src={banner.mediaUrl}
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="auto"
                        />
                      ) : (
                        <CachedImage
                          src={banner.mediaUrl}
                          alt={banner.title || "Promotion"}
                          className="kiosk-banner-media"
                        />
                      )}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          {/* On the pay-from-the-menu phone layout the rail already shows which category is open, so the
              title / count / subtitle is dropped while browsing. Search keeps its "Search results" heading. */}
          {(!BILLING || isSearching) && (
            <div className="kiosk-head">
              <h3>
                {isSearching ? "Search results" : activeCategory?.parentName}
                <span className="kiosk-head-count">{shownCount} shown</span>
              </h3>
              {isSearching ? (
                <p className="kiosk-head-sub">Results for “{trimmedSearch}”</p>
              ) : (
                activeCategory?.description && (
                  <p className="kiosk-head-sub">{activeCategory.description}</p>
                )
              )}
            </div>
          )}

          {!isSearching && availableSubs.length > 0 && (
            <div className="kiosk-chips" role="tablist">
              <button
                type="button"
                className={`kiosk-chip ${activeSub === ALL ? "on" : ""}`}
                onClick={() => selectSub(ALL)}
              >
                All
              </button>
              {availableSubs.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={`kiosk-chip ${activeSub === name ? "on" : ""}`}
                  onClick={() => selectSub(name)}
                >
                  {name}
                </button>
              ))}
            </div>
          )}

          {BILLING && vegActive && (
            <div className="kiosk-diet-note" role="status">
              <span className="kiosk-diet-mark" aria-hidden="true" />
              <span>Showing veg items only</span>
              <button type="button" onClick={() => setDiet("all")}>Show all</button>
            </div>
          )}

          <div className="kiosk-scroll" ref={listRef}>
            {shownCount > 0 ? (
              isSearching ? renderSearchResults() : renderItems()
            ) : (
              renderEmptyState()
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
